import { create, w, v } from '@dojo/framework/core/vdom';
import { dimensions } from '@dojo/framework/core/middleware/dimensions';
import { focus } from '@dojo/framework/core/middleware/focus';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { RenderResult } from '@dojo/framework/core/interfaces';
import { formatAriaProperties } from '@dojo/widgets/common/util';

import Icon from '@dojo/widgets/icon';
import theme from '@dojo/widgets/middleware/theme';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

import css from '../css/title-pane.m.css';
import fixedCss from '../css/title-pane-fixed.m.css';
import { isVariantTitle } from '../utils';
import { VariantTitle } from '../interfaces';

export interface TitlePaneProperties {
	/** QA key */
	xqa?: string;
	/** If false the pane will not collapse in response to clicking the title */
	closeable?: boolean;
	/** 'aria-level' for the title's DOM node */
	headingLevel?: number;
	/** If true the pane is opened and content is visible initially */
	initialOpen?: boolean;
	/** Explicitly control TitlePane */
	open?: boolean;
	/** Called when the title of a closed pane is clicked */
	onClose?(): void;
	/** Called when the title of an open pane is clicked */
	onOpen?(): void;
}

export interface TitlePaneICache {
	initialOpen?: boolean;
	open?: boolean;
}

export interface TitlePaneChildren {
	title: RenderResult | VariantTitle;
	content: RenderResult;
}

const icache = createICacheMiddleware<TitlePaneICache>();
const factory = create({
	dimensions,
	focus,
	icache,
	theme
})
	.properties<TitlePaneProperties>()
	.children<TitlePaneChildren | undefined>();

export const TitlePane = factory(({ id, children, properties, middleware: { dimensions, focus, icache, theme } }) => {
	const themeCss = theme.classes(css);
	const {
		closeable = true,
		headingLevel,
		initialOpen,
		onClose,
		onOpen,
		theme: themeProp,
		key = id,
		xqa
	} = properties();
	let { open } = properties();

	let { title, content } = children()[0] || ({} as TitlePaneChildren);
	if (isVariantTitle(title)) {
		title = open ? title.open : title.closed;
	}
	if (!Array.isArray(title)) {
		title = [title];
	}
	if (!Array.isArray(content)) {
		content = [content];
	}

	const firstRender = icache.get('open') === undefined;
	if (open === undefined) {
		open = icache.get('open');
		const existingInitialOpen = icache.get('initialOpen');

		if (initialOpen !== existingInitialOpen) {
			icache.set('open', initialOpen);
			icache.set('initialOpen', initialOpen);
			open = initialOpen;
		}
	}

	return v('div', { key, xqa, classes: [themeCss.root, open ? themeCss.open : null, fixedCss.rootFixed] }, [
		v(
			'div',
			{
				id: `${id}-title`,
				...formatAriaProperties({
					level: headingLevel ? `${headingLevel}` : null
				}),
				classes: [
					themeCss.title,
					closeable ? themeCss.closeable : null,
					fixedCss.titleFixed,
					closeable ? fixedCss.closeableFixed : null
				],
				role: 'heading'
			},
			[
				v(
					'button',
					{
						...formatAriaProperties({
							controls: `${id}-content`,
							expanded: open ? 'true' : 'false'
						}),
						disabled: !closeable,
						classes: [fixedCss.titleButtonFixed, themeCss.titleButton],
						focus: focus.isFocused('title-button'),
						key: 'title-button',
						onclick: (event: MouseEvent) => {
							event.stopPropagation();
							icache.set('open', !open);
							if (open) {
								onClose && onClose();
							} else {
								onOpen && onOpen();
							}
						},
						type: 'button'
					},
					[
						...title,
						v('span', { classes: [themeCss.arrow] }, [
							w(Icon, { type: open ? 'downIcon' : 'leftIcon', theme: themeProp })
						])
					]
				)
			]
		),
		v(
			'div',
			{
				...formatAriaProperties({
					hidden: open ? 'false' : 'true',
					labelledBy: `${id}-title`
				}),
				classes: [themeCss.content, !firstRender && themeCss.contentTransition, fixedCss.contentFixed],
				id: `${id}-content`,
				key: 'content',
				styles: { marginTop: open ? '0px' : `-${dimensions.get('content').offset.height}.px` }
			},
			content
		)
	]);
});

export default TitlePane;
